import React from 'react';

const clamp = (val, min, max) => Math.max(min, Math.min(max, val));
const fmtCurrency = (n) => '$' + new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(Number.isFinite(n) ? n : 0);
const parseMoney = (s) => {
  const cleaned = (s || '').toString().replace(/[^0-9.]/g, '');
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
};

class WealthGapQuestion extends React.Component {
  componentDidMount() {
    if (this.headingRef) {
      this.headingRef.focus();
    }
  }
  componentDidUpdate(prevProps) {
    // Only refocus on step change; do not steal focus while typing
    if (prevProps.step !== this.props.step && this.headingRef) {
      this.headingRef.focus();
    }
  }

  renderStep0() {
    const { values, onChange, onNext, saving, track, onSaveGoal } = this.props;
    const { goal_type, goal_lump_sum, annual_income, withdrawal_rate } = values;
    const handleRate = (e) => {
      const v = parseFloat(e.target.value || 0);
      onChange({ withdrawal_rate: clamp(v, 0.03, 0.06) });
      if (track) track('readiness_withdrawal_rate_changed', { rate: clamp(v, 0.03, 0.06) });
    };
    const handleNext = async () => {
      if (track) track('readiness_goal_type_selected', { type: goal_type });
      await (onSaveGoal && onSaveGoal());
      onNext();
    };
    return (
      <div className="w-full max-w-3xl mx-auto">
        <div className="mb-4">
          <button type="button" onClick={this.props.onBack} className="text-succession-primary hover:underline" aria-label="Go back">← Back</button>
        </div>
        <h1 className="text-3xl md:text-4xl font-semibold text-succession-foreground mb-2 focus:outline-none" tabIndex="-1" ref={(el) => (this.headingRef = el)}>
          Define your Wealth Goal
        </h1>
        <p className="text-succession-muted mb-4">This helps us estimate your Wealth Gap. You can change this later.</p>

        <div className="flex items-center space-x-6 text-succession-muted">
          <label>
            <input className="mr-2" type="radio" name="goal_type" value="lump_sum" aria-label="Lump Sum Target" checked={goal_type === 'lump_sum'} onChange={() => onChange({ goal_type: 'lump_sum' })} />
            <span>Lump Sum Target</span>
          </label>
          <label>
            <input className="mr-2" type="radio" name="goal_type" value="income" aria-label="Annual Income Target" checked={goal_type === 'income'} onChange={() => onChange({ goal_type: 'income' })} />
            <span>Annual Income Target (pre-tax)</span>
          </label>
        </div>

        {goal_type === 'lump_sum' ? (
          <div className="space-y-3 mt-6">
            <label className="block text-sm text-succession-muted">Lump Sum Target</label>
            <input
              type="text"
              inputMode="decimal"
              aria-describedby="lump_sum_help"
              className="border border-succession-border rounded p-3 w-full"
              value={fmtCurrency(goal_lump_sum || 0)}
              onChange={(e) => onChange({ goal_lump_sum: Math.max(0, parseMoney(e.target.value)) })}
            />
            <p id="lump_sum_help" className="text-xs text-succession-muted">Enter a non-negative amount.</p>
          </div>
        ) : (
          <>
            <div className="space-y-3 mt-6">
              <label className="block text-sm text-succession-muted">Annual Income Target (pre-tax)</label>
              <input
                type="text"
                inputMode="decimal"
                aria-describedby="annual_income_help"
                className="border border-succession-border rounded p-3 w-full"
                value={fmtCurrency(annual_income || 0)}
                onChange={(e) => onChange({ annual_income: Math.max(0, parseMoney(e.target.value)) })}
              />
              <p id="annual_income_help" className="text-xs text-succession-muted">We’ll convert this into a lump sum using your SWR.</p>
            </div>
            <div className="mt-4">
              <label className="block text-sm mb-2 text-succession-muted">Safe Withdrawal Rate (3% - 6%)</label>
              <div className="flex items-center space-x-3">
                <input type="range" min="0.03" max="0.06" step="0.01" className="flex-1" value={withdrawal_rate} onChange={handleRate} aria-label="Safe withdrawal rate slider" />
                <div className="w-16 text-right text-succession-foreground font-medium">{Math.round((withdrawal_rate || 0) * 100)}%</div>
              </div>
            </div>
          </>
        )}

        <div className="mt-6 flex justify-end">
          <button type="button" className="bg-succession-primary text-white px-4 py-2 rounded hover:bg-succession-primary/90" onClick={handleNext} disabled={saving} aria-label="Next">Next</button>
        </div>
      </div>
    );
  }

  renderStep1() {
    const { values, onChange, onNext, onBack, saving, track, onSaveAssets } = this.props;
    const toggleIlliquid = async (e) => {
      onChange({ include_illiquid: e.target.checked });
      if (track) track('readiness_toggle_include_illiquid', { include: e.target.checked });
      await (onSaveAssets && onSaveAssets({ include_illiquid: e.target.checked }));
    };
    const handleNext = async () => {
      if (track) track('readiness_view_completed', {});
      await (onSaveAssets && onSaveAssets());
      onNext();
    };
    return (
      <div className="w-full max-w-3xl mx-auto">
        <div className="mb-4">
          <button type="button" onClick={onBack} className="text-succession-primary hover:underline" aria-label="Go back">← Back</button>
        </div>
        <h1 className="text-3xl md:text-4xl font-semibold text-succession-foreground mb-2 focus:outline-none" tabIndex="-1" ref={(el) => (this.headingRef = el)}>
          Add your Investable Assets
        </h1>
        <p className="text-succession-muted mb-4">Exclude illiquid or non-core assets. You can change this later.</p>

        <div>
          <label className="block text-sm mb-2 text-succession-muted">Total Net Investable Assets</label>
          <input
            type="text"
            inputMode="decimal"
            className="border border-succession-border rounded p-3 w-full"
            value={fmtCurrency(values.current_investable_assets || 0)}
            onChange={(e) => onChange({ current_investable_assets: Math.max(0, parseMoney(e.target.value)) })}
            onBlur={() => onSaveAssets && onSaveAssets({ current_investable_assets: values.current_investable_assets })}
          />
        </div>

        <div className="border border-succession-border p-3 rounded bg-yellow-50 mt-4">
          <label className="inline-flex items-center text-succession-muted">
            <input className="mr-2" type="checkbox" checked={values.include_illiquid} onChange={toggleIlliquid} />
            <span>Include Illiquid/Non-Core Assets (I understand the risks of including illiquid assets in readiness planning)</span>
          </label>
        </div>

        <div className="mt-6 flex justify-end">
          <button type="button" className="bg-succession-primary text-white px-4 py-2 rounded hover:bg-succession-primary/90" onClick={handleNext} disabled={saving} aria-label="Next">See Results</button>
        </div>
      </div>
    );
  }

  render() {
    const { step } = this.props;
    return (
      <div className="flex justify-center bg-succession-surface px-4 pt-2 pb-6">
        {step === 0 ? this.renderStep0() : this.renderStep1()}
      </div>
    );
  }
}

export default WealthGapQuestion;


